/**
 * Live plant / person map pins · sticky LatLng across wheel zoom + pan.
 *
 * HARD RULE (must never regress — operator sees icons “run” under zoom if broken):
 *  1. MapLibre Marker owns screen transform (0×0 host · subpixelPositioning).
 *  2. Device poll must NOT destroy+recreate the whole pool — only upsert by id.
 *  3. While the camera is moving (wheel zoom / pan / rotate):
 *     - defer structural removes so markers never flash off under the gesture
 *     - NEVER call setLngLat from poll (geo freezes; screen rides MapLibre only)
 *  4. Soft silk RAF / sim also must not setLngLat while busy (soft-fleet-glyphs).
 *  5. After moveend/zoomend, one full reconcile applies pending LatLng + drops stale ids.
 *
 * Soft fleet has its own pool (soft-fleet-glyphs). This module is Live survey only.
 */

/** True while MapLibre camera is in motion (incl. trackpad / wheel zoom). */
export function opsMapCameraBusy(map) {
  if (!map) return false;
  if (map.__softCamBusy) return true;
  try {
    if (typeof map.isMoving === "function" && map.isMoving()) return true;
    if (typeof map.isZooming === "function" && map.isZooming()) return true;
    if (typeof map.isRotating === "function" && map.isRotating()) return true;
  } catch {
    /* */
  }
  return false;
}

/** Shell attribute for CSS freezes during gesture. */
function markGesturing(map, on) {
  if (!map) return;
  const v = on ? "1" : "0";
  try {
    const c = map.getContainer && map.getContainer();
    if (!c) return;
    c.setAttribute("data-gesturing", v);
    const host = c.closest && (c.closest(".map-host") || c.closest(".ops-map-shell"));
    if (host) host.setAttribute("data-gesturing", v);
    else if (c.parentElement) c.parentElement.setAttribute("data-gesturing", v);
  } catch {
    /* */
  }
}

function sameLl(a, b) {
  if (!a || !b) return false;
  const alng = Array.isArray(a) ? Number(a[0]) : Number(a.lng);
  const alat = Array.isArray(a) ? Number(a[1]) : Number(a.lat);
  const blng = Array.isArray(b) ? Number(b[0]) : Number(b.lng);
  const blat = Array.isArray(b) ? Number(b[1]) : Number(b.lat);
  if (![alng, alat, blng, blat].every(Number.isFinite)) return false;
  return Math.abs(alng - blng) < 1e-9 && Math.abs(alat - blat) < 1e-9;
}

function asLl(ll) {
  if (!ll) return null;
  if (Array.isArray(ll) && ll.length >= 2) {
    const lng = Number(ll[0]);
    const lat = Number(ll[1]);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }
  if (typeof ll === "object") {
    const lng = Number(ll.lng != null ? ll.lng : ll[0]);
    const lat = Number(ll.lat != null ? ll.lat : ll[1]);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  }
  return null;
}

function makePinMarker(maplibregl, ll, extra) {
  const el = document.createElement("div");
  el.style.width = el.style.height = "0";
  el.style.pointerEvents = "auto";
  return new maplibregl.Marker({
    element: el,
    anchor: "center",
    pitchAlignment: "viewport",
    rotationAlignment: "viewport",
    subpixelPositioning: true,
    ...(extra || {}),
  }).setLngLat(ll);
}

/**
 * Desired pin set from Live site state.
 * @returns {Map<string, {ll:number[], site?:boolean}>}
 */
export function opsLivePinWantOf({ devices, people, centre }) {
  const want = new Map();
  const c = asLl(centre);
  if (c) want.set("__site", { ll: c, site: true });
  for (const d of Array.isArray(devices) ? devices : []) {
    if (!d || !d.id) continue;
    const ll = asLl(d.ll);
    if (!ll) continue;
    want.set(String(d.id), { ll });
  }
  for (const p of Array.isArray(people) ? people : []) {
    if (!p || !p.id) continue;
    const ll = asLl(p.ll);
    if (!ll) continue;
    want.set("p:" + String(p.id), { ll });
  }
  return want;
}

/**
 * Upsert Live pin Markers. Stable hosts across polls so React portals stay put.
 *
 * @param {object} map
 * @param {Record<string, object>} pool
 * @param {{
 *   devices?:any[],
 *   people?:any[],
 *   centre?:any,
 *   maplibregl:any,
 *   forceStructural?:boolean,
 * }} opts
 * @returns {{ hosts: Record<string, HTMLElement>, deferredRemoves: string[] }}
 */
export function syncOpsLivePinPool(map, pool, opts) {
  const hosts = Object.create(null);
  const deferredRemoves = [];
  if (!map || !opts || !opts.maplibregl || !pool) {
    return { hosts, deferredRemoves };
  }
  const want = opsLivePinWantOf(opts);
  const busy = !opts.forceStructural && opsMapCameraBusy(map);

  for (const [id, meta] of want.entries()) {
    const ll = meta.ll;
    let mk = pool[id];
    if (!mk) {
      try {
        mk = makePinMarker(
          opts.maplibregl,
          ll,
          meta.site
            ? { pitchAlignment: "map", rotationAlignment: "viewport" }
            : null,
        ).addTo(map);
        mk.__ll = ll.slice();
        pool[id] = mk;
      } catch {
        continue;
      }
    } else if (!sameLl(mk.__ll, ll)) {
      if (busy) {
        /* Geo freeze under gesture · apply after moveend force. */
        mk.__pendingLl = ll.slice();
      } else {
        try {
          mk.setLngLat(ll);
          mk.__ll = ll.slice();
          mk.__pendingLl = null;
        } catch {
          /* */
        }
      }
    } else if (mk.__pendingLl && !busy) {
      try {
        mk.setLngLat(mk.__pendingLl);
        mk.__ll = mk.__pendingLl.slice();
        mk.__pendingLl = null;
      } catch {
        /* */
      }
    }
    try {
      const el = mk.getElement && mk.getElement();
      if (el) hosts[id] = el;
    } catch {
      /* */
    }
  }

  for (const id of Object.keys(pool)) {
    if (want.has(id)) continue;
    if (busy) {
      deferredRemoves.push(id);
      try {
        const el = pool[id].getElement && pool[id].getElement();
        if (el) hosts[id] = el;
      } catch {
        /* */
      }
      continue;
    }
    try {
      pool[id].remove();
    } catch {
      /* */
    }
    delete pool[id];
  }

  return { hosts, deferredRemoves };
}

/** Drop pool markers + clear record. */
export function clearOpsLivePinPool(pool) {
  if (!pool) return;
  for (const id of Object.keys(pool)) {
    try {
      pool[id].remove();
    } catch {
      /* */
    }
    delete pool[id];
  }
}

/**
 * One-shot map guards: reconcile Live pins after every camera gesture ends.
 * Soft fleet uses wireSoftUserCamGuard · this is Live-only.
 */
export function wireOpsLivePinCamera(map, applyFn) {
  if (!map || typeof applyFn !== "function") return;
  map.__opsLivePinApply = applyFn;
  if (map.__opsLivePinCamBound) return;
  map.__opsLivePinCamBound = true;
  const re = () => {
    try {
      markGesturing(map, false);
      if (typeof map.__opsLivePinApply === "function") map.__opsLivePinApply({ force: true });
    } catch {
      /* */
    }
  };
  const freeze = () => {
    markGesturing(map, true);
  };
  map.on("movestart", freeze);
  map.on("zoomstart", freeze);
  map.on("rotatestart", freeze);
  map.on("pitchstart", freeze);
  map.on("moveend", re);
  map.on("zoomend", re);
  map.on("rotateend", re);
  map.on("pitchend", re);
}
